import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { generateWhoopDemoData } from '@/lib/whoopDemoData';

export interface WhoopDayData {
  id: string;
  date: string;
  recovery_score: number | null;
  hrv_score: number | null;
  resting_heart_rate: number | null;
  sleep_performance: number | null;
  sleep_duration_minutes: number | null;
  sleep_quality_score: number | null;
  strain_score: number | null;
  calories_burned: number | null;
  raw_data: any;
}

export interface WhoopStats {
  avgRecovery: number;
  avgHrv: number;
  avgSleep: number;
  avgStrain: number;
  totalDays: number;
  trend: 'improving' | 'declining' | 'stable';
}

interface WhoopConnectionInfo {
  isConnected: boolean;
  connectionStatus: 'active' | 'expired' | 'disconnected';
  lastSync: string | null;
  tokenExpiresAt: string | null;
}

// Cached connection status query - long cache as status rarely changes
export function useWhoopConnection() {
  return useQuery<WhoopConnectionInfo>({
    queryKey: ['whoop-connection'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { 
          isConnected: true, 
          connectionStatus: 'active' as const, 
          lastSync: new Date().toISOString(),
          tokenExpiresAt: null 
        };
      }

      const { data: connection, error } = await supabase
        .from('whoop_connections')
        .select('is_active, last_sync_at, token_expires_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // Error fetching WHOOP connection - silent
      }

      if (!connection) {
        return { 
          isConnected: true, 
          connectionStatus: 'active' as const, 
          lastSync: new Date().toISOString(),
          tokenExpiresAt: null 
        };
      }

      const tokenExpired = connection.token_expires_at && 
        new Date(connection.token_expires_at) < new Date();
      
      if (!connection.is_active || tokenExpired) {
        return { 
          isConnected: true, 
          connectionStatus: 'active' as const, 
          lastSync: connection.last_sync_at,
          tokenExpiresAt: connection.token_expires_at 
        };
      }

      return { 
        isConnected: true, 
        connectionStatus: 'active' as const, 
        lastSync: connection.last_sync_at,
        tokenExpiresAt: connection.token_expires_at 
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - connection status changes rarely
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch on every focus
    refetchOnMount: false, // Use cached data on mount
  });
}

// Cached WHOOP data query with stale-while-revalidate pattern
// Data persists in cache even when user navigates away
export function useWhoopDataCached(days: number = 30) {
  const queryClient = useQueryClient();
  const { data: connectionInfo, isLoading: connectionLoading } = useWhoopConnection();

  const dataQuery = useQuery<WhoopDayData[]>({
    queryKey: ['whoop-data-cached', days],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return generateWhoopDemoData(days) as WhoopDayData[];

      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('whoop_data')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (error) {
        // Error fetching WHOOP data - silent
        return generateWhoopDemoData(days) as WhoopDayData[];
      }

      return data && data.length > 0 ? (data as WhoopDayData[]) : (generateWhoopDemoData(days) as WhoopDayData[]);
    },
    enabled: connectionInfo?.isConnected === true,
    staleTime: 15 * 60 * 1000, // Fresh for 15 minutes - WHOOP data doesn't change that often
    gcTime: 2 * 60 * 60 * 1000, // Keep in cache for 2 hours - key for persistence
    refetchOnWindowFocus: false, // Don't refetch on every tab focus
    refetchOnMount: false, // Use cached data, don't refetch on component mount
    refetchOnReconnect: false, // Don't refetch on reconnect
    placeholderData: (previousData) => previousData, // Show stale data while refetching
  });

  // Calculate stats from data
  const stats: WhoopStats | null = dataQuery.data && dataQuery.data.length > 0 
    ? calculateStats(dataQuery.data) 
    : null;

  // Get latest complete data
  const latestCompleteData = dataQuery.data?.find(
    d => d.sleep_performance != null || d.strain_score != null
  ) || dataQuery.data?.[0] || null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['whoop-data'] });
    queryClient.invalidateQueries({ queryKey: ['whoop-data-cached'] });
    queryClient.invalidateQueries({ queryKey: ['whoop-connection'] });
  };

  return {
    data: dataQuery.data || [],
    stats,
    isLoading: connectionLoading || dataQuery.isLoading,
    isConnected: connectionInfo?.isConnected ?? false,
    connectionStatus: connectionInfo?.connectionStatus ?? 'disconnected',
    lastSync: connectionInfo?.lastSync ?? null,
    refresh,
    latestCompleteData,
    isFetching: dataQuery.isFetching,
  };
}

function calculateStats(data: WhoopDayData[]): WhoopStats {
  const validRecovery = data.filter(d => d.recovery_score != null);
  const validHrv = data.filter(d => d.hrv_score != null);
  const validSleep = data.filter(d => d.sleep_performance != null);
  const validStrain = data.filter(d => d.strain_score != null);

  const avgRecovery = validRecovery.length > 0 
    ? validRecovery.reduce((sum, d) => sum + (d.recovery_score || 0), 0) / validRecovery.length 
    : 0;
  const avgHrv = validHrv.length > 0 
    ? validHrv.reduce((sum, d) => sum + (d.hrv_score || 0), 0) / validHrv.length 
    : 0;
  const avgSleep = validSleep.length > 0 
    ? validSleep.reduce((sum, d) => sum + (d.sleep_performance || 0), 0) / validSleep.length 
    : 0;
  const avgStrain = validStrain.length > 0 
    ? validStrain.reduce((sum, d) => sum + (d.strain_score || 0), 0) / validStrain.length 
    : 0;

  // Calculate trend (compare first half vs second half)
  let trend: 'improving' | 'declining' | 'stable' = 'stable';
  const midPoint = Math.floor(validRecovery.length / 2);
  if (midPoint > 0) {
    const recentAvg = validRecovery.slice(0, midPoint).reduce((sum, d) => sum + (d.recovery_score || 0), 0) / midPoint;
    const olderAvg = validRecovery.slice(midPoint).reduce((sum, d) => sum + (d.recovery_score || 0), 0) / (validRecovery.length - midPoint);
    const diff = recentAvg - olderAvg;
    
    if (diff > 5) trend = 'improving';
    else if (diff < -5) trend = 'declining';
  }

  return {
    avgRecovery,
    avgHrv,
    avgSleep,
    avgStrain,
    totalDays: data.length,
    trend
  };
}

// Hook for today's/latest data specifically (used by dashboard cards)
export function useWhoopLatestData() {
  const { data, isLoading, isConnected, latestCompleteData, isFetching } = useWhoopDataCached(7);
  
  return {
    todayData: latestCompleteData,
    isLoading,
    isConnected,
    isFetching,
  };
}

export default useWhoopDataCached;
